import { LoadingState } from '@v2/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    PiArrowLeftBold,
    PiCheckCircleBold,
    PiReceiptBold,
    PiWarningCircleBold,
} from 'react-icons/pi';
import FileDropzone from '@v2/components/ui/FileDropzone';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import type { CommissionRecord } from './types';

function fmt(v: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

export default function SubmitCommissionNfPage() {
    const { recordId } = useParams<{ recordId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const qc = useQueryClient();

    const fromCommissioning = location.pathname.includes('/commissioning/');
    const backTo = fromCommissioning
        ? '/commissioning/records?status=awaiting_nf'
        : '/my-commission';

    const { data, isLoading, isError, error } = useQuery<{ data: CommissionRecord }>({
        queryKey: ['commissioning', 'record', recordId],
        queryFn: () => api.get(`/commissioning/records/${recordId}`).then((r) => r.data),
        enabled: !!recordId,
    });

    const record = data?.data;

    const uploadMutation = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('invoice', file);
            return api.post(`/commissioning/records/${recordId}/submit-nf`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        onSuccess: () => {
            toast.success('NF enviada com sucesso!');
            qc.invalidateQueries({ queryKey: ['commissioning'] });
            navigate(backTo);
        },
        onError: () => toast.error('Erro ao enviar NF. Verifique o arquivo (PDF ou XML).'),
    });

    const canUpload = record && ['awaiting_nf', 'pending'].includes(record.status);
    const alreadySubmitted = record?.status === 'nf_submitted';
    const isPaid = record?.status === 'paid';

    return (
        <div className="mx-auto w-full max-w-lg pb-10">
            <div className="mb-6 flex items-center gap-2">
                <Link
                    to={backTo}
                    className="flex size-9 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800"
                >
                    <PiArrowLeftBold className="size-4" />
                </Link>
                <div>
                    <h1 className="text-lg font-bold text-gray-900 dark:text-white">Enviar Nota Fiscal</h1>
                    <p className="text-sm text-gray-500">Comissão · upload da NF do deal</p>
                </div>
            </div>

            {isLoading && <LoadingState variant="detail" />}

            {isError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    {(error as { response?: { data?: { message?: string } } })?.response?.data?.message
                        ?? 'Não foi possível carregar este registro de comissão.'}
                </div>
            )}

            {record && (
                <div className="space-y-4">
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="mb-3 flex flex-wrap gap-2">
                            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                                {record.status_label}
                            </span>
                            <span className="rounded-full bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-700">
                                {record.role_label}
                            </span>
                        </div>

                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Deal</p>
                        <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
                            {record.deal_number ?? `#${record.ploomes_deal_id}`}
                        </p>
                        {record.deal_title && (
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{record.deal_title}</p>
                        )}
                        {record.deal_amount != null && (
                            <p className="mt-2 text-sm text-gray-500">Valor do deal: {fmt(record.deal_amount)}</p>
                        )}

                        <div className="mt-4 flex items-end justify-between border-t border-gray-100 pt-4 dark:border-zinc-800">
                            <div>
                                <p className="text-xs text-gray-500">Comissão</p>
                                <p className="text-2xl font-black text-gray-900 dark:text-white">{fmt(record.final_amount)}</p>
                            </div>
                            {record.user_name && fromCommissioning && (
                                <p className="text-right text-sm text-gray-500">
                                    Vendedor<br />
                                    <span className="font-medium text-gray-800 dark:text-gray-200">{record.user_name}</span>
                                </p>
                            )}
                        </div>

                        {record.days_until_nf_deadline != null && record.status === 'awaiting_nf' && (
                            <div className={cn(
                                'mt-4 rounded-xl px-4 py-3 text-sm',
                                record.days_until_nf_deadline <= 3
                                    ? 'bg-red-50 text-red-800 border border-red-200'
                                    : 'bg-amber-50 text-amber-900 border border-amber-200',
                            )}>
                                <p className="font-semibold">
                                    {record.days_until_nf_deadline > 0
                                        ? `${record.days_until_nf_deadline} dia(s) restantes para enviar a NF`
                                        : 'Prazo para NF expirado — envie o quanto antes'}
                                </p>
                                {record.nf_deadline_at && (
                                    <p className="mt-0.5 text-xs opacity-80">
                                        Prazo: {record.nf_deadline_at.split(' ')[0]}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {canUpload && (
                        <div className="rounded-2xl border border-orange-200 bg-orange-50/50 p-5 dark:border-orange-900 dark:bg-orange-950/20">
                            <div className="mb-4 flex items-start gap-3">
                                <PiReceiptBold className="mt-0.5 size-5 shrink-0 text-orange-600" />
                                <div>
                                    <p className="font-semibold text-gray-900 dark:text-white">
                                        Envie a NF para liberar o pagamento
                                    </p>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                        Aceito PDF ou XML da Nota Fiscal. O pagamento só será processado após o envio.
                                    </p>
                                </div>
                            </div>

                            <FileDropzone
                                accept=".pdf,.xml,application/pdf,application/xml,text/xml"
                                acceptLabel="PDF ou XML"
                                maxSizeMb={10}
                                loading={uploadMutation.isPending}
                                disabled={uploadMutation.isPending}
                                label="Arraste a NF aqui"
                                hint="ou clique para selecionar o arquivo"
                                onFiles={(files) => {
                                    if (files[0]) uploadMutation.mutate(files[0]);
                                }}
                                onError={(msg) => toast.error(msg)}
                            />
                        </div>
                    )}

                    {alreadySubmitted && (
                        <div className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                            <PiCheckCircleBold className="size-5 shrink-0" />
                            NF já enviada — aguardando liberação do financeiro.
                        </div>
                    )}

                    {isPaid && (
                        <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                            <PiCheckCircleBold className="size-5 shrink-0" />
                            Comissão já paga.
                        </div>
                    )}

                    {record.status === 'cancelled' && (
                        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                            <PiWarningCircleBold className="size-5 shrink-0" />
                            Este registro de comissão foi cancelado.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
